import { IEventEmitter, IEventSource } from "../lib/eventing/events";
import { WorkbookClassificationEvents, WorkbookClassificationSource } from "./workbookClassification";
import { WorksheetClassificationEvents, WorksheetClassificationSource } from "./worksheetClassification";

export type Enhancement = {
    readonly type: string;
    readonly restored: boolean;
}

export type ProgressiveEnhancementEvents = {
    worksheetProcessed: { workbookId: string, worksheetId: string, workbookCode: string, worksheetCode:string, enhancements: { failed: Enhancement[], succeeded: Enhancement[] } }
}
export type ProgressiveEnhancementEmitter = IEventEmitter<ProgressiveEnhancementEvents>;
export type ProgressiveEnhancementSource = IEventSource<ProgressiveEnhancementEvents>;

export type ProgressiveEnhancement<TInput = any> = {
    type: string;
    inputs: TInput;
}

export type ProgressiveEnhancementProcessor<TInput = any> = (
    workbookId: string,
    worksheetId: string,
    input: TInput,
    restore: boolean
) => Promise<void>;

export type ProgressiveEnhancementRuleMap = Record<string, ProgressiveEnhancement[]>; // WorksheetCode -> Enhancements

export type WorkbookProgressiveEnhancementFetcher = (workbookCode: string) => Promise<ProgressiveEnhancementRuleMap | undefined>;

export class ProgressiveEnhancementService {
    private processors: Record<string, ProgressiveEnhancementProcessor> = {};
    private currentWorkbookCode: string | undefined;

    constructor(
        private readonly rulesFetcher: WorkbookProgressiveEnhancementFetcher,
        private readonly workbookClassificationEvents: WorkbookClassificationSource,
        private readonly worksheetClassificationEvents: WorksheetClassificationSource,
        private readonly enhancementEmitter: ProgressiveEnhancementEmitter
    ) {
        this.workbookClassificationEvents.events.workbookClassified.on(this.handleWorkbookClassified.bind(this));
        this.worksheetClassificationEvents.events.worksheetClassified.on(this.handleWorksheetClassified.bind(this));
    }

    public registerProcessor(type: string, processor: ProgressiveEnhancementProcessor) {
        this.processors[type] = processor;
    }

    private handleWorkbookClassified(event: WorkbookClassificationEvents['workbookClassified']) {
        this.currentWorkbookCode = event.code;
    }

    private async handleWorksheetClassified(event: WorksheetClassificationEvents['worksheetClassified']) {
        if (!this.currentWorkbookCode) {
            console.warn("Worksheet classified but workbook code is unknown.");
            return;
        }
        const enhancements = await this.applyEnhancementsToWorksheet(this.currentWorkbookCode, event.code, event.workbookId, event.worksheetId);
        this.enhancementEmitter.events.worksheetProcessed.emit({
            workbookId: event.workbookId,
            worksheetId: event.worksheetId,
            workbookCode: this.currentWorkbookCode,
            worksheetCode: event.code,
            enhancements
        });
    }
    private async applyEnhancementsToWorksheet(workbookCode:string, worksheetCode: string, workbookId: string, worksheetId: string): Promise<{succeeded: Enhancement[], failed:Enhancement[]}> {
        const rulesMap = await this.rulesFetcher(workbookCode);
        if (!rulesMap) return {succeeded: [], failed: []};

        const enhancements = rulesMap[worksheetCode];
        if (!enhancements || enhancements.length === 0) return {succeeded: [], failed: []};

        //TODO: fetch saved applied enhancements

        const failedEnhancements: Enhancement[] = [];
        const appliedEnhancements: Enhancement[] = [];
        for (const enhancement of enhancements) {
            const restored = false; //TODO: restoration, is this enhancement in the saved applied enhancements?
            const processor = this.processors[enhancement.type];
            if (processor) {
                try {
                    await processor(workbookId, worksheetId, enhancement.inputs, restored);
                    appliedEnhancements.push({
                        type: enhancement.type,
                        restored
                    });
                } catch (error) {
                    console.error(`Error applying progressive enhancement ${enhancement.type} for worksheet ${worksheetCode}:`, error);
                    failedEnhancements.push({
                        type: enhancement.type,
                        restored
                    });
                }
            } else {
                failedEnhancements.push({
                    type: enhancement.type,
                    restored
                });
                console.warn(`No processor registered for progressive enhancement type: ${enhancement.type}`);
            }
        }
        //TODO: save applied enhancements
        return {succeeded: appliedEnhancements, failed: failedEnhancements};
    };
}

export interface CreateNamedRangesInput {
    ranges: Record<string, { address: string, comment?: string }>;
    prefix?: string;
}
export type CreateNamedRangesProgressiveEnhancement = ProgressiveEnhancement<CreateNamedRangesInput>;

export const createNamedRangesProcessor = (defaultPrefix: string): ProgressiveEnhancementProcessor<CreateNamedRangesInput> => async (_workbookId, worksheetId, input) => {
    //TODO: consider making this leverage the excel relay to make it's changes... effectively upsert named ranges
    const prefix = input.prefix ?? defaultPrefix;
    await Excel.run(async (context) => {
        const worksheet = context.workbook.worksheets.getItem(worksheetId);
        const rangesToProcess = Object.entries(input.ranges);
        
        // Check which ranges already exist
        const namedItems = rangesToProcess.map(([name]) => {
            const fullName = `${prefix}${name}`;
            return { 
                fullName, 
                item: worksheet.names.getItemOrNullObject(fullName) 
            };
        });
        
        await context.sync();
        
        for (let i = 0; i < rangesToProcess.length; i++) {
            const { fullName, item } = namedItems[i];
            const [_, details] = rangesToProcess[i];

            if (item.isNullObject) {
                const range = worksheet.getRange(details.address);
                worksheet.names.add(fullName, range, details.comment);
            }
        }
        await context.sync();
    });
}
